import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { logError } from "../lib/log";
import { useCart } from "../context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) return setState("error");
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setOrder(r.data.order);
          setState("paid");
          clear();
          return;
        }
        if (["expired", "failed"].includes(r.data.payment_status)) return setState("error");
      } catch (err) {
        logError("Payment status polling failed:", err);
      }
      if (attempts.current >= 10) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div data-testid="payment-success-page" className="max-w-2xl mx-auto px-6 py-24 text-center">
      {state === "polling" && (
        <>
          <Clock className="w-14 h-14 mx-auto text-[#7DC242] animate-pulse" />
          <h1 className="font-serif-display font-bold text-3xl mt-6">Verifica del pagamento...</h1>
          <p className="mt-3 text-[#78716C]">Attendi qualche secondo, stiamo confermando il tuo ordine.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 data-testid="payment-confirmed-icon" className="w-14 h-14 mx-auto text-[#3C6E53]" />
          <h1 className="font-serif-display font-bold text-3xl mt-6">Grazie! Ordine confermato</h1>
          <p className="mt-3 text-[#78716C]">
            Il pagamento è andato a buon fine{order ? ` — ordine di ${order.total.toFixed(2).replace(".", ",")} €` : ""}. Riceverai i tuoi piatti con spedizione refrigerata.
          </p>
          {order && (
            <div className="mt-8 rounded-2xl border border-[#E7E0D8] bg-white p-6 text-left">
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#78716C] mb-3">Ordine #{order.id.slice(0, 8)}</p>
              {order.items.map((i) => (
                <div key={i.slug} className="flex justify-between text-sm py-1.5">
                  <span>{i.name} × {i.quantity}</span>
                  <span className="font-mono-accent">{(i.price * i.quantity).toFixed(2).replace(".", ",")} €</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {(state === "error" || state === "timeout") && (
        <>
          <XCircle className="w-14 h-14 mx-auto text-[#2E8A57]" />
          <h1 className="font-serif-display font-bold text-3xl mt-6">{state === "timeout" ? "Verifica in corso" : "Pagamento non confermato"}</h1>
          <p className="mt-3 text-[#78716C]">{state === "timeout" ? "La conferma sta impiegando più del previsto. Controlla la tua email o riprova più tardi." : "Qualcosa è andato storto con il pagamento."}</p>
        </>
      )}
      <Link to="/prodotti" data-testid="back-to-shop-btn" className="btn-tuscan inline-block mt-10 px-8 py-4 rounded-full text-sm font-semibold">Torna al menu</Link>
    </div>
  );
}
